import { perkText } from '@/lib/levelPerks';
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { History } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { formatDistanceToNow } from 'date-fns';
import { getDateFnsLocale } from '@/lib/dateLocale';
import { useAuth } from '@/features/auth/AuthProvider';
import { useGamification } from '@/hooks/useGamification';
import { useActiveChild } from '@/contexts/ActiveChildContext';

export const PointsDropdown: React.FC = () => {

    const { currentUser } = useAuth();
    const { activeChildId } = useActiveChild();
    const { fetchPointHistory, getUserLevel, levels, loading: gamificationLoading } = useGamification();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [transactions, setTransactions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    // State to hold the effective user data (Parent or Active Child)
    const [displayUser, setDisplayUser] = useState<any>(currentUser);

    // Update display user when activeChildId or currentUser changes
    useEffect(() => {
        const updateDisplayUser = async () => {
            if (activeChildId && activeChildId !== currentUser?.uid) {
                try {
                    const { doc, getDoc } = await import('firebase/firestore');
                    const { db } = await import('@/lib/firebase');
                    const userDoc = await getDoc(doc(db, 'users', activeChildId));
                    if (userDoc.exists()) {
                        setDisplayUser({ uid: userDoc.id, ...userDoc.data() });
                    }
                } catch (err) {
                    console.error('Error fetching child profile for points:', err);
                    setDisplayUser(currentUser);
                }
            } else {
                setDisplayUser(currentUser);
            }
        };
        updateDisplayUser();
    }, [activeChildId, currentUser]);

    useEffect(() => {
        const loadHistory = async () => {
            if (displayUser) {
                setLoading(true);
                const history = await fetchPointHistory(displayUser.uid);
                // Limit to 5 for dropdown
                setTransactions(history.slice(0, 5));
                setLoading(false);
            }
        };
        loadHistory();
    }, [displayUser, fetchPointHistory]);

    const handleViewHistory = () => {
        setIsOpen(false);
        navigate('/points');
    };

    if (!displayUser) return null;

    const totalScore = displayUser.totalScore || 0;
    const levelInfo = getUserLevel(totalScore);

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-auto py-2 rounded-2xl border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-900 px-4 flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                        <span className="font-bold">{totalScore.toLocaleString()}</span>
                        <span className="text-xs text-amber-600">xp</span>
                        {levelInfo.current && (
                            <>
                                <span className="text-amber-300">|</span>
                                <span className="text-xs font-semibold text-amber-700">lvl {levelInfo.current.level}</span>
                            </>
                        )}
                    </div>
                    {/* Progress bar to next level */}
                    {levelInfo.next && (
                        <div className="w-full h-1.5 bg-amber-200 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-300"
                                style={{ width: `${levelInfo.progress}%` }}
                            />
                        </div>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
                {/* Level Progress Section */}
                {levels.length > 0 && (
                    <>
                        <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-t-lg">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <span className="text-2xl">{levelInfo.current?.icon || '🌱'}</span>
                                    <div>
                                        <p className="font-bold text-sm" style={{ color: levelInfo.current?.color }}>
                                            {levelInfo.current ? `Level ${levelInfo.current.level}: ${levelInfo.current.name}` : 'Getting Started'}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {totalScore.toLocaleString()} XP
                                        </p>
                                    </div>
                                </div>
                                {levelInfo.next && (
                                    <div className="text-end">
                                        <span className="text-lg">{levelInfo.next.icon}</span>
                                        <p className="text-[10px] text-muted-foreground">Next</p>
                                    </div>
                                )}
                            </div>
                            {levelInfo.next ? (
                                <div className="space-y-1">
                                    <Progress value={levelInfo.progress} className="h-2" />
                                    <p className="text-[10px] text-muted-foreground text-center">
                                        {levelInfo.next.pointsRequired - totalScore} XP to {levelInfo.next.name}
                                    </p>
                                </div>
                            ) : (
                                <div className="text-center">
                                    <p className="text-xs text-amber-600 font-medium">🎉 Max Level Reached!</p>
                                </div>
                            )}
                            {(levelInfo.current?.perks ?? []).length > 0 && (
                                <div className="mt-3 border-t border-amber-200 pt-2">
                                    <p className="text-[11px] font-semibold text-amber-800">{t('gamification.level_perks_title')}</p>
                                    <ul className="mt-1 space-y-0.5 text-xs text-slate-700">
                                        {levelInfo.current!.perks!.map((p, i) => <li key={i}>• {perkText(p, t, i18n.language)}</li>)}
                                    </ul>
                                </div>
                            )}
                        </div>
                        <DropdownMenuSeparator />
                    </>
                )}

                <DropdownMenuLabel className="flex justify-between items-center">
                    <span>Recent History</span>
                    <span className="text-xs text-muted-foreground font-normal">Last 5 Transactions</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <ScrollArea className="h-[160px]">
                    {loading || gamificationLoading ? (
                        <div className="p-4 text-center text-xs text-muted-foreground">Loading history...</div>
                    ) : transactions.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-[120px] text-muted-foreground text-xs p-4 text-center">
                            <span className="text-2xl font-bold text-amber-200 mb-2">0 xp</span>
                            <p>No XP yet.</p>
                            <p className="mt-1">Start training to earn!</p>
                        </div>
                    ) : (
                        <div className="p-1 space-y-1">
                            {transactions.map((tx) => (
                                <div key={tx.id} className="flex items-center justify-between p-2 rounded-sm hover:bg-muted/50 transition-colors">
                                    <div className="flex-1 min-w-0 me-2">
                                        <p className="text-sm font-medium truncate">{tx.description}</p>
                                        <p className="text-[10px] text-muted-foreground capitalize">
                                            {tx.type.replace('_', ' ')} • {formatDistanceToNow(tx.createdAt, { addSuffix: true, locale: getDateFnsLocale() })}
                                        </p>
                                    </div>
                                    <div className={`font-bold text-xs ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {tx.amount > 0 ? '+' : ''}{tx.amount}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </ScrollArea>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    className="cursor-pointer justify-center text-primary font-medium gap-2"
                    onClick={handleViewHistory}
                >
                    <History className="h-4 w-4" />
                    View Full History
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

